// ---------------------------------------------------------------------------
// VariableAutocomplete — reusable Combobox for state variable selection.
//
// Two modes:
//   - "key-only": Shows all symbols on focus, filters on keystroke.
//                 Used by SetStateForm (State Key) and ApiCallForm (Response Key).
//   - "expression": Shows suggestions only after `#{` is typed.
//                   Used by ExpressionForm for inline symbol references.
// ---------------------------------------------------------------------------

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useAllSymbols, useSymbolSuggestions } from '@/features/flow-editor/hooks/use-symbol-suggestions'
import {
  findLegacyExpressionPrefixes,
  LEGACY_PREFIX_HINTS,
} from '@/features/flow-editor/lib/expression-prefixes'

// Help text for dot-terminated prefix stubs — picking one inserts an OPEN
// `#{prefix.` (no closing brace) because the path still needs finishing.
const PREFIX_STUB_HELP: Record<string, string> = {
  'local.': "this block's variables",
  'appData.': 'app-wide shared state',
  'computed.': 'derived values',
  'event.': 'event payload',
  'props.': 'block inputs',
  'env.': 'configuration (read-only)',
}

function stubHelp(symbol: string): string | null {
  if (!symbol.endsWith('.')) return null
  const known = PREFIX_STUB_HELP[symbol]
  return known ? `${known} — finish the path` : 'finish the path'
}

export interface VariableAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  mode: 'key-only' | 'expression'
  /**
   * Phase 378 Plan 02 Task 1 — optional data-testid for Playwright selectors.
   * Applied to the inner <input> so tests can target parameter inputs by
   * `[data-testid="param-input-<key>"]`.
   */
  testid?: string
  /**
   * Optional override of the suggestion pool. Without it, suggestions come
   * from the canonical context-derived vocabulary (useAllSymbols). Forms with
   * a narrower contract (e.g. setState target = bare variable names) pass
   * their own list.
   */
  suggestions?: string[]
}

export function VariableAutocomplete({
  value,
  onChange,
  onBlur,
  placeholder,
  mode,
  testid,
  suggestions: suggestionsOverride,
}: VariableAutocompleteProps) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [cursorPos, setCursorPos] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // ---------------------------------------------------------------------------
  // Symbol sources
  // ---------------------------------------------------------------------------

  const contextSymbols = useAllSymbols()
  const allSymbols = suggestionsOverride ?? contextSymbols
  const expressionSuggestions = useSymbolSuggestions(value, cursorPos, suggestionsOverride)

  const suggestions = useMemo(() => {
    if (mode === 'key-only') {
      if (!showDropdown) return []
      if (!value) return allSymbols
      return allSymbols.filter((s) =>
        s.toLowerCase().includes(value.toLowerCase()),
      )
    }
    // expression mode
    return expressionSuggestions ?? []
  }, [mode, value, allSymbols, expressionSuggestions, showDropdown])

  // Legacy-prefix inline education (non-blocking): the validator rejects
  // state./localState./response./ui./params./page. — warn at typing time
  // instead of at validation time. Implemented once here so every
  // VariableAutocomplete-backed input gets it for free.
  const legacyPrefixes = useMemo(() => findLegacyExpressionPrefixes(value), [value])

  // ---------------------------------------------------------------------------
  // Dropdown visibility
  // ---------------------------------------------------------------------------

  const openDropdown = useCallback(() => {
    setShowDropdown(true)
    setHighlightIdx(0)
  }, [])

  const closeDropdown = useCallback(() => {
    setShowDropdown(false)
  }, [])

  // Close on outside click
  useEffect(() => {
    if (!showDropdown) return
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        closeDropdown()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDropdown, closeDropdown])

  // ---------------------------------------------------------------------------
  // Selection handlers
  // ---------------------------------------------------------------------------

  const selectItem = useCallback((symbol: string) => {
    if (mode === 'key-only') {
      onChange(symbol)
      closeDropdown()
      return
    }
    // expression mode — insert #{symbol} at cursor
    const textBefore = value.slice(0, cursorPos)
    const hashMatch = textBefore.match(/#{([^}]*)$/)
    if (!hashMatch) return
    const beforeHash = textBefore.slice(0, hashMatch.index! + 2)
    const after = value.slice(cursorPos)
    if (symbol.endsWith('.')) {
      // Prefix stub (`event.`, `props.`, …): the path is incomplete — a
      // closing brace would commit an invalid expression like `#{event.}`.
      // Insert OPEN, keep the dropdown alive, and put the caret right after
      // the dot so continued typing stays inside the expression.
      const newValue = `${beforeHash}${symbol}${after}`
      const caret = beforeHash.length + symbol.length
      onChange(newValue)
      setCursorPos(caret)
      setHighlightIdx(0)
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(caret, caret)
      })
      return
    }
    const newValue = `${beforeHash}${symbol}}${after}`
    onChange(newValue)
    closeDropdown()
  }, [mode, value, cursorPos, onChange, closeDropdown])

  // ---------------------------------------------------------------------------
  // Keyboard navigation (D-06)
  // ---------------------------------------------------------------------------

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (suggestions[highlightIdx]) {
        e.preventDefault()
        selectItem(suggestions[highlightIdx])
      }
    } else if (e.key === 'Escape') {
      closeDropdown()
    }
  }, [showDropdown, suggestions, highlightIdx, selectItem, closeDropdown])

  // ---------------------------------------------------------------------------
  // Input change
  // ---------------------------------------------------------------------------

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    setCursorPos(e.target.selectionStart ?? newValue.length)
    if (mode === 'key-only') {
      openDropdown()
    } else {
      // expression mode: open only if `#{` detected
      const textBefore = newValue.slice(0, e.target.selectionStart ?? newValue.length)
      if (textBefore.match(/#{([^}]*)$/)) {
        openDropdown()
      } else {
        closeDropdown()
      }
    }
  }, [onChange, mode, openDropdown, closeDropdown])

  const handleFocus = useCallback(() => {
    if (mode === 'key-only') {
      openDropdown()
    }
  }, [mode, openDropdown])

  const handleBlur = useCallback(() => {
    // Delay to allow mousedown on dropdown to fire first
    setTimeout(() => {
      onBlur?.()
    }, 150)
  }, [onBlur])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const showSuggestions = showDropdown && suggestions.length > 0

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        role="textbox"
        data-testid={testid}
        className="w-full text-xs bg-transparent border border-input rounded-md px-2 py-1 focus:border-primary focus:ring-1 focus:ring-ring outline-none font-mono"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />

      {showSuggestions && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg py-1"
        >
          {suggestions.map((symbol, idx) => (
            <div
              key={symbol}
              className={cn(
                'px-2 py-1 text-xs font-mono hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors',
                idx === highlightIdx && 'bg-accent text-accent-foreground',
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                selectItem(symbol)
              }}
            >
              {symbol}
              {mode === 'expression' && stubHelp(symbol) && (
                <span className="ml-1.5 font-sans text-[9px] italic text-muted-foreground">
                  {stubHelp(symbol)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && suggestions.length === 0 && value && mode === 'key-only' && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg py-1">
          <div className="px-2 py-1 text-[10px] text-muted-foreground italic">
            No matching variables.
          </div>
        </div>
      )}

      {legacyPrefixes.length > 0 && (
        <div
          data-testid="legacy-prefix-warning"
          className="mt-0.5 text-[9px] text-amber-600 dark:text-amber-400"
        >
          {legacyPrefixes
            .map((p) => `"${p}." is a legacy prefix — use ${LEGACY_PREFIX_HINTS[p]}`)
            .join(' · ')}
        </div>
      )}
    </div>
  )
}
